import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import { getFormInputProps } from "util/forms";
import { InputableRadioGroup, OnboardingInputControl } from "ui/molecules/Input";
import OnboardingForm from "../../OnboardingForm";
import { FormField, type OnboardingRiskAndComplianceProps } from "./types";

const RiskAndCompliance = ({
    values,
    isSubmitting,
    isValid,
    isBootstrapDataLoading,
    isIncomplete,
    requestedChangeFields,
    bootstrapData,
    setFieldValue,
    setFieldTouched,
    handleChange,
    handleBlur,
    handleSubmit,
    ...form
}: OnboardingRiskAndComplianceProps) => (
    <OnboardingForm
        {...form}
        isSubmitting={isSubmitting}
        isValid={isValid}
        isIncomplete={isIncomplete}
        requestedChangeFields={requestedChangeFields}
        handleSubmit={handleSubmit}
    >
        <OnboardingInputControl
            name={FormField.HasTerminationHistory}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <InputableRadioGroup
                    slotProps={{
                        typography: {
                            children: 'Have any of your agreements been terminated by an acquiring company?*'
                        },
                        formControlLabel: {
                            disabled
                        },
                        radioGroup: {
                            'aria-labelledby': FormField.HasTerminationHistory,
                            name: FormField.HasTerminationHistory,
                            value: values[FormField.HasTerminationHistory],
                            onChange: handleChange,
                            onBlur: handleBlur
                        },
                        textField: {
                            ...getFormInputProps(FormField.TerminationHistoryInput, form),
                            disabled,
                            name: FormField.TerminationHistoryInput,
                            value: values[FormField.TerminationHistoryInput],
                            label: 'If YES: Quote the name & explain the reason',
                            onChange: handleChange,
                            onBlur: handleBlur
                        }
                    }}
                />
            )}
        />
        <OnboardingInputControl
            name={FormField.HasCreditLimitPolicy}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <InputableRadioGroup
                    slotProps={{
                        typography: {
                            children: 'Do you have a credit limit policy on your product? (if applicable)*'
                        },
                        formControlLabel: {
                            disabled
                        },
                        radioGroup: {
                            'aria-labelledby': FormField.HasCreditLimitPolicy,
                            name: FormField.HasCreditLimitPolicy,
                            value: values[FormField.HasCreditLimitPolicy],
                            onChange: handleChange,
                            onBlur: handleBlur
                        },
                        textField: {
                            ...getFormInputProps(FormField.CreditLimitPolicyInput, form),
                            disabled,
                            name: FormField.CreditLimitPolicyInput,
                            value: values[FormField.CreditLimitPolicyInput],
                            label: 'IF YES: Provide Details',
                            onChange: handleChange,
                            onBlur: handleBlur
                        }
                    }}
                />
            )}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Detail all fraud tools used
        </Typography>
        <OnboardingInputControl
            name={FormField.FraudToolsInput}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <TextField
                    {...getFormInputProps(FormField.FraudToolsInput, form)}
                    required
                    multiline
                    fullWidth
                    disabled={disabled}
                    rows={4}
                    size='small'
                    name={FormField.FraudToolsInput}
                    value={values[FormField.FraudToolsInput]}
                    label='Comma separated list of fraud tools used'
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
            )}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Please provide the name of the 3rd party KYC provider(s)/service(s) you use and also an example of the results they provide for one of your players.
        </Typography>
        <OnboardingInputControl
            name={FormField.KycProviderInput}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <TextField
                    {...getFormInputProps(FormField.KycProviderInput, form)}
                    required
                    multiline
                    fullWidth
                    disabled={disabled}
                    rows={4}
                    size='small'
                    name={FormField.KycProviderInput}
                    value={values[FormField.KycProviderInput]}
                    label='KYC Provider Name and Details'
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
            )}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            What are your refund & chargeback policies?
        </Typography>
        <OnboardingInputControl
            name={FormField.RefundChargebackPolicyInput}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <TextField
                    {...getFormInputProps(FormField.RefundChargebackPolicyInput, form)}
                    required
                    multiline
                    fullWidth
                    disabled={disabled}
                    rows={4}
                    size='small'
                    name={FormField.RefundChargebackPolicyInput}
                    value={values[FormField.RefundChargebackPolicyInput]}
                    label='Refund & Chargeback Policies'
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
            )}
        />
        <OnboardingInputControl
            name={FormField.RefundChargebackPolicyInput}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <InputableRadioGroup
                    slotProps={{
                        typography: {
                            children: 'Any additional concerns/risk issues? (Optional)'
                        },
                        formControlLabel: {
                            disabled
                        },
                        radioGroup: {
                            'aria-labelledby': FormField.RefundChargebackPolicyInput,
                            name: FormField.RefundChargebackPolicyInput,
                            value: values[FormField.RefundChargebackPolicyInput],
                            onChange: handleChange,
                            onBlur: handleBlur
                        },
                        textField: {
                            ...getFormInputProps(FormField.RefundChargebackPolicyInput, form),
                            disabled,
                            name: FormField.RefundChargebackPolicyInput,
                            value: values[FormField.RefundChargebackPolicyInput],
                            label: 'IF YES: Provide Details',
                            onChange: handleChange,
                            onBlur: handleBlur
                        }
                    }}
                />
            )}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Describe briefly the security features & risk management measures you will adopt
        </Typography>
        <OnboardingInputControl
            name={FormField.SecurityFeaturesInput}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <TextField
                    {...getFormInputProps(FormField.SecurityFeaturesInput, form)}
                    required
                    multiline
                    fullWidth
                    disabled={disabled}
                    rows={4}
                    size='small'
                    name={FormField.SecurityFeaturesInput}
                    value={values[FormField.SecurityFeaturesInput]}
                    label='e.g., Proprietary or Third-Party fraud scrubbing'
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
            )}
        />
        <OnboardingInputControl
            name={FormField.HasSsl}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <InputableRadioGroup
                    slots={{
                        textField: <></>
                    }}
                    slotProps={{
                        typography: {
                            sx: {
                                mt: 2
                            },
                            children: 'Is your Site/Payment Page SSL secured? (Optional)'
                        },
                        formControlLabel: {
                            disabled
                        },
                        radioGroup: {
                            'aria-labelledby': FormField.HasSsl,
                            name: FormField.HasSsl,
                            value: values[FormField.HasSsl],
                            onChange: handleChange,
                            onBlur: handleBlur
                        }
                    }}
                />
            )}
        />
    </OnboardingForm>
);

export default RiskAndCompliance;
